import { getFrameTypeIcon } from "@/components/ui/concept-icons";
import {
  Suspense,
  lazy,
  memo,
  useMemo,
  useState,
  type ComponentType,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { Button, Icons } from "@/components/ui";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { useT } from "@/i18n";
import { framesConversationMessages } from "@/i18n/messages/frames-conversation";
import { cn } from "@/lib/cn";
import { frameTypeLabel } from "../shared/frame-type-label";
import { presentArtifactBesideConversation } from "@/lib/conversation-artifact-presentation";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { frameNodeSize } from "@/lib/frame-node-geometry";
import { useCanvasStore } from "@/stores/canvas-store";
import { useFrame } from "@/stores/frames-selectors";
import { useShallow } from "zustand/react/shallow";
import { isDocContent } from "../doc-frame/doc-content";
import { DocEmbedFrame } from "../doc-frame/doc-embed-frame";
import { EventFrame } from "../event-frame/event-frame";
import { AgendaFrame } from "../agenda-frame/agenda-frame";
import { WeatherFrame } from "../weather-frame/weather-frame";
import { FrameDetailLoading } from "../shared/frame-detail-loading";
import { InlineFrameEmbedContext } from "../shared/inline-frame-embed-context";

type BaseFrameComponent = ComponentType<FrameProps>;

const HtmlFrame = lazy(() =>
  import("../html-frame").then((m) => ({ default: m.HtmlFrame })),
);
const CodeFrame = lazy(() =>
  import("../code-frame").then((m) => ({ default: m.CodeFrame })),
);
const ImageFrame = lazy(() =>
  import("../image-frame").then((m) => ({ default: m.ImageFrame })),
);
const EmailFrame = lazy(() =>
  import("../email-frame").then((m) => ({ default: m.EmailFrame })),
);

/** Docs (and their former `note`/`markdown` types) embed read-only. */
const EMBEDDABLE_FRAMES: Record<string, BaseFrameComponent> = {
  doc: DocEmbedFrame,
  note: DocEmbedFrame,
  markdown: DocEmbedFrame,
  event: EventFrame,
  agenda: AgendaFrame,
  weather: WeatherFrame,
  html: HtmlFrame,
  code: CodeFrame,
  image: ImageFrame,
  email: EmailFrame,
};

interface ConversationFrameEmbedProps {
  frameId: string;
  conversationFrameId?: string;
}

/**
 * Inline frame embed for production conversation threads.
 *
 * Minimal chrome by design: just the BASE frame component content (never the
 * canvas wrap() HOC chain, so no gesture targets, resize controls, or
 * context menus leak into the thread) plus a single always-visible arrow
 * button that presents the frame beside the conversation. Clicking the
 * preview body toggles inline expand/collapse. Expanded, content-sized
 * frames grow to fit; box-filling frames (HTML) take the canvas node's
 * proportions. The element tree is identical in both states so an
 * embedded iframe never reloads on toggle.
 *
 * The card is a faint outline that highlights on hover, and the collapsed
 * preview fades out at the bottom so it reads as a clickable card rather
 * than cropped text. The card is the only box: an embedded frame draws no
 * surface of its own (`useFrameSurface`), and the card's one radius shapes
 * the outline, the hover fill, and the content clip. Code-block fills are
 * cleared so the artifact reads on the conversation's glass.
 */
export const ConversationFrameEmbed = memo(function ConversationFrameEmbed({
  frameId,
  conversationFrameId,
}: ConversationFrameEmbedProps) {
  const t = useT(framesConversationMessages);
  const [expanded, setExpanded] = useState(false);
  const frame = useFrame(frameId);
  // HTML fills its box instead of sizing to its content, so an expanded
  // HTML embed takes the canvas node's proportions.
  const proportional = expanded && frame?.frameType === "html";
  const nodeSize = useCanvasStore(
    useShallow((state) => {
      if (!proportional) return null;
      const node = state.nodes.find((candidate) => candidate.id === frameId);
      return node ? frameNodeSize(node) : null;
    }),
  );

  const frameProps = useMemo<FrameProps | null>(
    () => (frame ? { id: frameId, data: frame, selected: false } : null),
    [frameId, frame],
  );

  if (!frame || !frameProps) return null;

  const frameType = frame.frameType;
  const title = frameDisplayTitle(frame);
  // Any V4 content is a Doc's, whatever the frame's type.
  const FrameComponent = isDocContent(frame.content)
    ? DocEmbedFrame
    : (EMBEDDABLE_FRAMES[frameType] ?? null);
  const embedTestId = `conversation-embed-${frameId}`;

  const handleOpenBeside = (event: ReactMouseEvent) => {
    event.stopPropagation();
    if (!conversationFrameId) return;
    presentArtifactBesideConversation(conversationFrameId, frameId);
  };

  const handleToggleExpand = () => {
    setExpanded((previous) => !previous);
  };

  const handleToggleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggleExpand();
    }
  };

  let preview: ReactNode;
  try {
    // A card-only frame mounts no body until its detail has loaded.
    preview = !FrameComponent ? (
      <ConversationEmbedFallback
        frameId={frameId}
        frameType={frameType}
        title={title}
      />
    ) : frame.contentState === "card" ? (
      <FrameDetailLoading frame={frame} />
    ) : (
      <FrameComponent {...frameProps} />
    );
  } catch {
    preview = (
      <ConversationEmbedFallback
        frameId={frameId}
        frameType={frameType}
        title={title}
      />
    );
  }

  return (
    <div
      className={cn(
        "relative w-full min-w-0 overflow-hidden rounded-preview border border-[var(--color-embed-card-stroke)] transition-colors hover:bg-[var(--color-interactive-hover)]",
        "[&_[data-frame-content=true]_pre]:!bg-transparent",
      )}
      data-testid={embedTestId}
    >
      {conversationFrameId ? (
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          onClick={handleOpenBeside}
          aria-label={t("openFrameBeside")}
          data-testid={`${embedTestId}-open`}
          className="absolute right-2 top-2 z-10"
        >
          <Icons.ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      ) : null}

      <ErrorBoundary
        fallback={
          <ConversationEmbedFallback
            frameId={frameId}
            frameType={frameType}
            title={title}
          />
        }
      >
        <div
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={handleToggleExpand}
          onKeyDown={handleToggleKeyDown}
          className="w-full min-w-0 cursor-pointer outline-none focus-visible:bg-[var(--color-interactive-hover)]"
        >
          <div
            className={cn(
              "relative w-full min-w-0",
              !expanded &&
                "nodrag nowheel pointer-events-none h-28 overflow-hidden [mask-image:linear-gradient(to_bottom,black_55%,transparent)]",
              proportional &&
                "min-h-40 max-h-[min(60vh,520px)] overflow-hidden",
            )}
            style={
              nodeSize
                ? { aspectRatio: `${nodeSize.width} / ${nodeSize.height}` }
                : undefined
            }
            aria-hidden={!expanded}
            data-testid={`${embedTestId}-content`}
          >
            <div
              className={cn(
                proportional ? "absolute inset-0" : "h-full w-full",
              )}
              data-testid={`${embedTestId}-preview`}
            >
              <InlineFrameEmbedContext.Provider value>
                <Suspense
                  fallback={<ConversationEmbedLoading frameId={frameId} />}
                >
                  {preview}
                </Suspense>
              </InlineFrameEmbedContext.Provider>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </div>
  );
});

function ConversationEmbedLoading({ frameId }: { frameId: string }) {
  const t = useT(framesConversationMessages);
  return (
    <div
      className="flex h-16 items-center justify-center"
      data-testid={`conversation-embed-${frameId}-loading`}
      aria-label={t("loadingFramePreview")}
    >
      <Icons.Spinner className="h-3.5 w-3.5 animate-spin text-muted" />
    </div>
  );
}

function ConversationEmbedFallback({
  frameId,
  frameType,
  title,
}: {
  frameId: string;
  frameType: string;
  title: string;
}) {
  const t = useT(framesConversationMessages);
  const FrameIcon = getFrameTypeIcon(frameType);
  const typeLabel = frameTypeLabel(frameType);
  return (
    <div
      className="flex items-center gap-2.5 rounded-preview px-3 py-2.5"
      data-testid={`conversation-embed-${frameId}-fallback`}
      role="note"
      aria-label={t("embedFallbackLabel", { title, type: typeLabel })}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-preview bg-glass-fill-dense">
        <FrameIcon className="h-4 w-4 text-muted" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-appkit-caption font-semibold text-primary">
          {title}
        </span>
        <span className="block truncate text-canvas-xs text-muted">
          {t("frameOfType", { type: typeLabel })}
        </span>
      </span>
    </div>
  );
}
